import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Bell, UserPlus, Users, Dumbbell, MessageCircle,
  Sparkles, CheckCircle, X, Loader2, Inbox,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const TYPE_META = {
  follow_request: { icon: UserPlus, color: 'text-blue-400', label: 'Follow Request' },
  buddy_request: { icon: Users, color: 'text-orange-400', label: 'Buddy Request' },
  buddy_workout_invite: { icon: Dumbbell, color: 'text-orange-400', label: 'Workout Invite' },
  buddy_workout_accepted: { icon: CheckCircle, color: 'text-green-400', label: 'Workout Joined' },
  buddy_workout_completed: { icon: CheckCircle, color: 'text-green-400', label: 'Workout Done' },
  workout_assigned: { icon: Dumbbell, color: 'text-primary', label: 'New Workout' },
  welcome_message: { icon: Sparkles, color: 'text-yellow-400', label: 'Welcome' },
  new_user: { icon: UserPlus, color: 'text-purple-400', label: 'New Member' },
  default: { icon: Bell, color: 'text-muted-foreground', label: 'Notification' },
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'follow_request', label: 'Follows' },
  { key: 'buddy', label: 'Buddies' },
  { key: 'workouts', label: 'Workouts' },
  { key: 'system', label: 'System' },
];

const FILTER_GROUPS = {
  all: () => true,
  follow_request: (n) => n.type === 'follow_request',
  buddy: (n) => ['buddy_request'].includes(n.type),
  workouts: (n) => ['buddy_workout_invite', 'buddy_workout_accepted', 'buddy_workout_completed', 'workout_assigned'].includes(n.type),
  system: (n) => ['welcome_message', 'new_user'].includes(n.type),
};

function ActivityRow({ notif, onAccept, onReject, onDismiss, busyId }) {
  const meta = TYPE_META[notif.type] || TYPE_META.default;
  const Icon = meta.icon;
  const userName = notif.user?.name || 'Someone';
  const isBusy = busyId === notif.id;

  const isFollowRequest = notif.type === 'follow_request';
  const isBuddyRequest = notif.type === 'buddy_request';
  const isWorkoutInvite = notif.type === 'buddy_workout_invite';

  const linkTo = (() => {
    if (isFollowRequest || isBuddyRequest) return notif.user?.id ? `/user/${notif.user.id}` : null;
    // Live freestyle invites have workout_id = "live" — there's no workout page
    // for that, so fall through to the live-workout preflight via the invite_id below.
    if (notif.workout_id && notif.workout_id !== 'live') return `/workouts/${notif.workout_id}`;
    return null;
  })();

  return (
    <div className="bg-card border border-border p-4 flex gap-4 items-start" data-testid={`activity-row-${notif.id}`}>
      <div className={`shrink-0 mt-0.5 ${meta.color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-xs font-heading uppercase tracking-wider text-muted-foreground">{meta.label}</span>
          {notif.created_at && (
            <span className="text-xs text-muted-foreground">·</span>
          )}
          {notif.created_at && (
            <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}</span>
          )}
        </div>
        <p className="text-white text-sm break-words">{notif.message || `${userName} sent you a ${meta.label.toLowerCase()}`}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(isFollowRequest || isBuddyRequest || isWorkoutInvite) && (
            <>
              <Button
                size="sm"
                onClick={() => onAccept(notif)}
                disabled={isBusy}
                className="bg-primary text-black hover:bg-primary/90"
                data-testid={`activity-accept-${notif.id}`}
              >
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-1" /> Accept</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onReject(notif)}
                disabled={isBusy}
                data-testid={`activity-reject-${notif.id}`}
              >
                <X className="h-4 w-4 mr-1" /> Decline
              </Button>
            </>
          )}
          {isWorkoutInvite && notif.invite_id && (
            <Link to={`/live-workout/${notif.invite_id}`}>
              <Button size="sm" className="bg-red-600 text-white hover:bg-red-700">
                <span className="inline-block h-2 w-2 rounded-full bg-white mr-1.5 animate-pulse" />
                Join Live
              </Button>
            </Link>
          )}
          {linkTo && (
            <Link to={linkTo}>
              <Button size="sm" variant="ghost" className="text-muted-foreground">View</Button>
            </Link>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDismiss(notif)}
            disabled={isBusy}
            className="text-muted-foreground hover:text-white"
            data-testid={`activity-dismiss-${notif.id}`}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ActivityPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await api().get('/social/notifications');
      const list = res.data.notifications || [];
      list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setNotifications(list);
    } catch (err) {
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const filtered = useMemo(
    () => notifications.filter(FILTER_GROUPS[filter] || FILTER_GROUPS.all),
    [notifications, filter],
  );
  const counts = useMemo(() => {
    const out = { all: notifications.length };
    for (const [k, fn] of Object.entries(FILTER_GROUPS)) {
      if (k === 'all') continue;
      out[k] = notifications.filter(fn).length;
    }
    return out;
  }, [notifications]);

  const handleAccept = async (notif) => {
    setBusyId(notif.id);
    try {
      if (notif.type === 'follow_request') {
        await api().post(`/social/follow-requests/${notif.id}/accept`);
        toast.success('Follow request accepted');
      } else if (notif.type === 'buddy_request') {
        await api().post(`/buddies/requests/${notif.id}/accept`);
        toast.success('Buddy request accepted!');
      } else if (notif.type === 'buddy_workout_invite' && notif.invite_id) {
        await api().post(`/buddies/workout-invite/${notif.invite_id}/accept`);
        toast.success(`Joined ${notif.workout_title || 'the workout'}!`);
        // Auto-dismiss the invite once accepted
        try { await api().post(`/social/notifications/${notif.id}/dismiss`); } catch { /* silent */ }
      }
      setNotifications(curr => curr.filter(n => n.id !== notif.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
    setBusyId(null);
  };

  const handleReject = async (notif) => {
    setBusyId(notif.id);
    try {
      if (notif.type === 'follow_request') {
        await api().post(`/social/follow-requests/${notif.id}/reject`);
      } else if (notif.type === 'buddy_request') {
        await api().post(`/buddies/requests/${notif.id}/reject`);
      } else if (notif.type === 'buddy_workout_invite' && notif.invite_id) {
        await api().post(`/buddies/workout-invite/${notif.invite_id}/decline`);
        try { await api().post(`/social/notifications/${notif.id}/dismiss`); } catch { /* silent */ }
      }
      toast.success('Declined');
      setNotifications(curr => curr.filter(n => n.id !== notif.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
    setBusyId(null);
  };

  const handleDismiss = async (notif) => {
    setBusyId(notif.id);
    try {
      await api().post(`/social/notifications/${notif.id}/dismiss`);
      setNotifications(curr => curr.filter(n => n.id !== notif.id));
    } catch (err) {
      toast.error('Failed to dismiss');
    }
    setBusyId(null);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="activity-page">
      <header className="border-b border-border px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-white" data-testid="activity-back-btn" aria-label="back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Bell className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-2xl uppercase tracking-wider">Activity</h1>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6">
        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-6 px-6 border-b border-border">
          {FILTERS.map(({ key, label }) => {
            const c = counts[key] || 0;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`shrink-0 px-3 py-1.5 text-xs font-heading uppercase tracking-wider rounded-full border transition-colors
                  ${filter === key ? 'bg-primary text-black border-primary' : 'bg-card text-muted-foreground border-border hover:text-white'}`}
                data-testid={`activity-filter-${key}`}
              >
                {label}{c > 0 && <span className="ml-1.5 opacity-80">{c}</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="activity-empty">
            <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              {filter === 'all' ? "You're all caught up." : 'Nothing here right now.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((notif) => (
              <ActivityRow
                key={notif.id}
                notif={notif}
                onAccept={handleAccept}
                onReject={handleReject}
                onDismiss={handleDismiss}
                busyId={busyId}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
